import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import { PersonalizedSlipTemplatesContext } from "../../common/print-batch-modal";
import { useSelectedPersonalizedSlipSizes } from "../../hooks/use-selected-slip-sizes";

export const FulfillmentPersonalizedSlipTemplateCard = memo(
  function FulfillmentPersonalizedSlipTemplateCard({
    fulfillmentGroups,
    updateRows,
    everythingDisabled,
    canPurchaseEveryGroup,
  }: {
    fulfillmentGroups: readonly FulfillmentGroupData[];
    updateRows: (updates: FulfillmentGroupUpdate[]) => void;
    everythingDisabled?: boolean;
    canPurchaseEveryGroup?: boolean;
  }) {
    const personalizedSlipTemplates = useContext(
      PersonalizedSlipTemplatesContext,
    );

    const mixedPersonalizedSlipSizes =
      useSelectedPersonalizedSlipSizes(
        personalizedSlipTemplates,
        fulfillmentGroups,
      ).size > 1;

    const mixedPersonalizedSlips =
      personalizedSlipTemplates.filter((template) =>
        fulfillmentGroups.some(
          (fg) => fg.personalizedSlipTemplateId === template._id,
        ),
      ).length > 1;

    // Get the currently selected template ID(s)
    const selectedTemplateIds = useMemo(() => {
      const templateIds = fulfillmentGroups
        .map((fg) => fg.personalizedSlipTemplateId)
        .filter((id): id is string => id != null);
      return [...new Set(templateIds)];
    }, [fulfillmentGroups]);

    const selectedTemplateId = mixedPersonalizedSlips
      ? undefined
      : selectedTemplateIds[0];

    const templateOptions: RedoListItem<string>[] = useMemo(() => {
      const options: RedoListItem<string>[] = personalizedSlipTemplates.map(
        (template) => ({
          id: template._id,
          value: template._id,
          type: "text" as const,
          text: template.name,
        }),
      );

      options.unshift({
        id: "",
        value: "",
        type: "text" as const,
        text: "No personalized insert",
      });

      return options;
    }, [personalizedSlipTemplates]);

    const onTemplateChange = useHandler(
      ({ item }: { item: RedoListItem<string> }) => {
        const templateId = item.value;
        updateRows(
          fulfillmentGroups.map(({ _id }) => ({
            fulfillmentGroupId: _id,
            personalizedSlipTemplateId: templateId || null,
          })),
        );
      },
    );

    // The slip PDF is rendered asynchronously after selection; show a loading
    // state until the generation workflow finishes and clears the marker.
    const isGenerating = fulfillmentGroups.some(
      (fg) => !!fg.personalizedSlipGeneratingSince,
    );

    // A generated slip is per-group, so only offer "View insert" for a single
    // selected group that already has one.
    const generatedSlipUrl =
      fulfillmentGroups.length === 1
        ? fulfillmentGroups[0]?.personalizedSlipGeneratedUrl
        : undefined;

    const onViewInsert = useHandler(() => {
      if (generatedSlipUrl) {
        window.open(generatedSlipUrl, "_blank", "noopener,noreferrer");
      }
    });

    return (
      <Flex dir="column" gap="xl">
        <RedoSingleSelectDropdownInput
          disabled={
            everythingDisabled || !canPurchaseEveryGroup || isGenerating
          }
          options={templateOptions}
          optionSelected={onTemplateChange}
          placeholder={
            mixedPersonalizedSlips
              ? "Mixed personalized inserts"
              : selectedTemplateId
                ? undefined
                : "No personalized insert"
          }
          selectedItem={templateOptions.find(
            (option) => option.value === selectedTemplateId,
          )}
          size="xs"
        />
        {mixedPersonalizedSlipSizes && (
          <Text fontSize="xs" textColor="warning">
            Multiple personalized insert sizes selected
          </Text>
        )}
        {isGenerating && (
          <Flex align="center" gap="xs">
            <CircleSpinner />
            <Text fontSize="xs" textColor="tertiary">
              Generating personalized insert…
            </Text>
          </Flex>
        )}
        {!isGenerating && generatedSlipUrl && (
          <Flex>
            <RedoButton
              hierarchy="secondary"
              IconLeading={LinkExternalIcon}
              onClick={onViewInsert}
              size="xs"
              text="View insert"
            />
          </Flex>
        )}
      </Flex>
    );
  },
);
